import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import Icon from '@/components/ui/AppIcon';

interface MonthlyProjection {
  month: string;
  revenue: number;
  expenses: number;
  profit: number;
}

interface IncomeProjectionsProps {
  monthlyData: MonthlyProjection[];
  totalInvestment: number;
  breakEvenMonth: number;
  yearlyProfit: number;
}

const IncomeProjections = ({ monthlyData, totalInvestment, breakEvenMonth, yearlyProfit }: IncomeProjectionsProps) => {
  const formatCurrency = (value: number) => `$${value.toFixed(0)}`;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-soft">
          <p className="font-medium text-foreground mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.dataKey === 'revenue' && 'Revenue: '}
              {entry.dataKey === 'expenses' && 'Expenses: '}
              {entry.dataKey === 'profit' && 'Profit: '}
              {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Income Projections</h3>
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="CurrencyDollarIcon" size={16} />
          <span>12-Month Forecast</span>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-surface rounded-lg p-4 text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Icon name="BanknotesIcon" size={20} className="text-primary" />
            <span className="text-sm font-medium text-muted-foreground">Initial Investment</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{formatCurrency(totalInvestment)}</p>
        </div>

        <div className="bg-surface rounded-lg p-4 text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Icon name="CalendarIcon" size={20} className="text-warning" />
            <span className="text-sm font-medium text-muted-foreground">Break Even</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{breakEvenMonth} months</p>
        </div>

        <div className="bg-surface rounded-lg p-4 text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Icon name="TrendingUpIcon" size={20} className="text-success" />
            <span className="text-sm font-medium text-muted-foreground">Yearly Profit</span>
          </div>
          <p className="text-2xl font-bold text-success">{formatCurrency(yearlyProfit)}</p>
        </div>
      </div>

      {/* Monthly Revenue Chart */}
      <div className="mb-6">
        <h4 className="text-md font-semibold text-foreground mb-4">Monthly Revenue & Expenses</h4>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="month" 
                tick={{ fontSize: 12, fill: '#6b7280' }}
                axisLine={{ stroke: '#d1d5db' }}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#6b7280' }}
                axisLine={{ stroke: '#d1d5db' }}
                tickFormatter={formatCurrency}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="revenue" fill="#4A7C59" name="Revenue" radius={[2, 2, 0, 0]} />
              <Bar dataKey="expenses" fill="#E76F51" name="Expenses" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Profit Trend */}
      <div>
        <h4 className="text-md font-semibold text-foreground mb-4">Profit Trend</h4>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={monthlyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="month" 
                tick={{ fontSize: 12, fill: '#6b7280' }}
                axisLine={{ stroke: '#d1d5db' }}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#6b7280' }}
                axisLine={{ stroke: '#d1d5db' }}
                tickFormatter={formatCurrency}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="profit" 
                stroke="#2D5A27" 
                strokeWidth={3}
                dot={{ fill: '#2D5A27', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#2D5A27', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default IncomeProjections;